import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import { Skeleton } from '@art-of-hospitality-solutions-llc/ui-kit/components/skeleton';
import service from '../../service';
import { formatMoney, formatValue } from './utils';

export type EntityContext = {
  index: string;
  hit: Record<string, any>;
  fishId: string | null;
};

export type EntityCard = {
  id: string;
  shouldRender: (ctx: EntityContext) => boolean;
  Component: React.FC<EntityContext>;
};

/** The fish a record points at: explicit fishId, or the record's own id when it IS a fish. */
export function resolveFishId(index: string, hit: Record<string, any>): string | null {
  if (hit?.fishId) return String(hit.fishId);
  if (index === 'fish') return hit?.id ? String(hit.id) : null;
  return null;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-zinc-100 py-1.5 last:border-0">
      <span className="text-xs font-medium text-zinc-500">{label}</span>
      <span className="max-w-[60%] truncate text-right text-sm">{children}</span>
    </div>
  );
}

// ─── Cards ───

const RawRecordCard: React.FC<EntityContext> = ({ hit }) => {
  const entries = Object.entries(hit).filter(([, v]) => v != null && typeof v !== 'object');
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Record</CardTitle>
      </CardHeader>
      <CardContent>
        {entries.map(([k, v]) => (
          <Row key={k} label={k}>
            {formatValue(v)}
          </Row>
        ))}
      </CardContent>
    </Card>
  );
};

const CheckItemsCard: React.FC<EntityContext> = ({ hit }) => {
  const items: any[] = hit?.typeMeta?.checkItems ?? [];
  if (!items.length) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Items ordered ({items.length})</CardTitle>
      </CardHeader>
      <CardContent>
        {items.map((item, i) => (
          <div
            key={item.itemId ?? i}
            className="flex justify-between gap-4 border-b border-zinc-100 py-1.5 last:border-0"
          >
            <span className="text-sm">
              {item.quantity != null ? `${item.quantity}× ` : ''}
              {item.name ?? '—'}
              {item.category && <span className="ml-2 text-xs text-zinc-400">{item.category}</span>}
            </span>
            <span className="text-sm">{formatMoney(item.price)}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const GuestCard: React.FC<EntityContext> = ({ fishId }) => {
  // Pull the guest summary from the `fish` index (the superset that includes guests), not `members`.
  const { data, isPending, isError } = useQuery({
    queryKey: ['omni-guest', fishId],
    queryFn: () =>
      service.omniSearch({
        indexes: ['fish'],
        perIndex: { fish: { filters: [{ field: 'id', op: 'eq', value: fishId }] } },
        limit: 1,
        countUniqueFish: false,
      }),
    enabled: !!fishId,
  });

  if (isPending) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Guest</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  const guest = data?.results?.[0]?.hits?.[0];
  if (isError || !guest) return null;

  const tags: Array<{ name?: string } | string> = guest.tags ?? [];

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-sm">Guest</CardTitle>
        <Link to={`/guests/${fishId}`} className="text-xs text-blue-600 hover:underline">
          Open profile →
        </Link>
      </CardHeader>
      <CardContent>
        <Row label="Name">{guest.fullName ?? '—'}</Row>
        <Row label="Phone">{guest.phoneNumber ?? '—'}</Row>
        <Row label="Active subscription">
          <Badge variant={guest.hasActiveSubscription ? 'default' : 'outline'}>
            {guest.hasActiveSubscription ? 'Active' : 'Inactive'}
          </Badge>
        </Row>
        {guest.subscriptionTier && <Row label="Tier">{guest.subscriptionTier}</Row>}
        {guest.lifetimeSpend != null && <Row label="Member App Spend">{formatMoney(guest.lifetimeSpend)}</Row>}
        {tags.length > 0 && (
          <Row label="Tags">
            {tags.map((t) => (typeof t === 'string' ? t : t.name)).filter(Boolean).join(', ')}
          </Row>
        )}
      </CardContent>
    </Card>
  );
};

const FishActivityCard: React.FC<EntityContext> = ({ fishId }) => {
  const { data, isPending, isError } = useQuery({
    queryKey: ['omni-fish-activity', fishId],
    queryFn: async () => {
      const res = await service.getActivity(fishId!, false);
      if (!res.ok) throw new Error('Failed to load activity');
      return res.json();
    },
    enabled: !!fishId,
  });

  if (isError) return null;

  const items: any[] = data?.data ?? [];
  const meta = data?.meta;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Recent activity</CardTitle>
      </CardHeader>
      <CardContent>
        {isPending ? (
          <Skeleton className="h-20 w-full" />
        ) : (
          <>
            {meta && (
              <div className="mb-2 text-xs text-zinc-500">
                MTD {formatValue(meta.mtdTotal)} · YTD {formatValue(meta.ytdTotal)} · All{' '}
                {formatValue(meta.allTotal)}
              </div>
            )}
            {items.length === 0 ? (
              <div className="text-sm text-zinc-500">No activity.</div>
            ) : (
              items.slice(0, 6).map((a, i) => (
                <div key={a.identifier ?? i} className="border-b border-zinc-100 py-1.5 last:border-0">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">{a.type}</span>
                    <span>{formatValue(a.price)}</span>
                  </div>
                  <div className="text-xs text-zinc-500">
                    {a.typeDescriptor ?? a.status}
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Card registry. Cards self-activate via `shouldRender`. To surface new info about an entity later,
 * add one entry here — no other wiring is required.
 */
export const ENTITY_CARDS: EntityCard[] = [
  { id: 'raw-record', shouldRender: () => true, Component: RawRecordCard },
  {
    id: 'check-items',
    shouldRender: (c) => Array.isArray(c.hit?.typeMeta?.checkItems) && c.hit.typeMeta.checkItems.length > 0,
    Component: CheckItemsCard,
  },
  { id: 'guest-summary', shouldRender: (c) => !!c.fishId, Component: GuestCard },
  { id: 'fish-activity', shouldRender: (c) => !!c.fishId, Component: FishActivityCard },
];
